import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  signal,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  AccessUser,
  SiteOption,
  BackofficePasswordMode,
} from '../../../../../core/models/system-settings/access-users.model';
import { AccessRoleDto } from '../../../../../core/models/system-settings/manage-roles.model';
import { ClientIInfo } from '../../../../../core/models/parking.model';
import { ApiSystemSettingsService } from '../../../../../core/services/api/system-settings/api-system-settings.service';
import { ApiCardQuotaService } from '../../../../../core/services/api/card-quota/api-card-quota.service';
import { ApiService } from '../../../../../core/services/api.service';
import { MapperAccessUsersService } from '../../../../../core/mappers/system-settings/mapper-access-users.service';
import { MapperManageRolesService } from '../../../../../core/mappers/system-settings/mapper-manage-roles.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-manage-user',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-manage-user.component.html',
  styleUrl: './modal-manage-user.component.css',
})
export class ModalManageUserComponent implements OnInit {
  @Input() data: AccessUser | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  displayName = '';
  login = '';
  email = '';
  phone = '';
  cpRole = 0;
  stampAllowed = false;
  stampRanges = false;
  stampMany = false;
  active = true;
  cityLoginEnabled = true;
  maxConcurrentSessionsText = '';
  hasCityPassword = false;
  mustChangePassword = true;

  passwordMode: BackofficePasswordMode = 'self_set';
  password = '';
  confirmPassword = '';

  resetPasswordMode: BackofficePasswordMode = 'self_set';
  resetPassword = '';
  resetConfirmPassword = '';
  resetMustChangePassword = true;
  isResettingPassword = signal(false);

  showCreatePassword = false;
  showCreateConfirmPassword = false;
  showResetPassword = false;
  showResetConfirmPassword = false;

  roleOptions = signal<{ id: number; name: string }[]>([]);
  siteOptions = signal<SiteOption[]>([]);
  selectedSiteIds = new Set<number>();
  searchSiteText = '';

  isLoading = signal<boolean>(false);
  isOptionsLoading = signal<boolean>(false);
  isClientAccessLoading = signal<boolean>(false);
  isSitesLoading = signal<boolean>(false);

  searchRoleText = '';
  searchClientText = '';
  openDropdown: string | null = null;

  siteClients: ClientIInfo[] = [];
  selectedClientIds = new Set<number>();
  clientAccessUnrestricted = true;

  private toast = inject(ToastService);
  private apiService = inject(ApiSystemSettingsService);
  private apiCardQuota = inject(ApiCardQuotaService);
  private apiLegacy = inject(ApiService);
  private userMapper = inject(MapperAccessUsersService);
  private roleMapper = inject(MapperManageRolesService);
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private permissionService = inject(MenuPermissionService);
  readonly siteState = inject(SiteStateService);

  ngOnInit() {
    this.login = this.data?.login || '';
    this.displayName = this.data?.displayName || '';
    this.cpRole = this.data?.cpRole || 0;
    this.stampAllowed = !!this.data?.stampAllowed;
    this.stampRanges = !!this.data?.stampRanges;
    this.stampMany = !!this.data?.stampMany;
    this.active = this.data?.active !== false;
    this.cityLoginEnabled = this.data?.cityLoginEnabled !== false;
    this.hasCityPassword = !!this.data?.hasCityPassword;
    this.mustChangePassword = this.data?.mustChangePassword !== false;
    this.selectedSiteIds = new Set(
      this.parseSiteIds(this.data?.siteIdStr || ''),
    );
    this.loadRoleOptions();
    this.loadSiteOptions();
    if (this.data?.id) {
      this.loadDetail(this.data.id);
      this.loadClientAccessData();
    }
  }

  get isCreate(): boolean {
    return !this.data?.id;
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get filteredSiteOptions(): SiteOption[] {
    const query = (this.searchSiteText || '').toLowerCase().trim();
    const options = this.siteOptions();
    if (!query) return options;
    return options.filter((s) => s.name.toLowerCase().includes(query));
  }

  get selectedSiteCount(): number {
    return this.selectedSiteIds.size;
  }

  closeDropdowns(): void {
    if (this.openDropdown === 'role') {
      const options = this.roleOptions() || [];
      const match = options.find(
        (o) =>
          o?.name?.toLowerCase() === this.searchRoleText?.trim()?.toLowerCase(),
      );

      if (match) {
        this.cpRole = match.id;
        this.searchRoleText = match.name;
      } else {
        const currentRole = options.find((o) => o.id === this.cpRole);
        this.searchRoleText = currentRole ? currentRole.name : '';
      }
    }
    this.openDropdown = null;
  }

  get filteredRoleOptions(): { id: number; name: string }[] {
    const query = (this.searchRoleText || '').toLowerCase().trim();
    const options = this.roleOptions() || [];
    if (!query) return options;
    return options.filter((o) => o?.name?.toLowerCase().includes(query));
  }

  get filteredSiteClients(): ClientIInfo[] {
    const query = (this.searchClientText || '').toLowerCase().trim();
    const sorted = [...this.siteClients].sort((a, b) =>
      a.name.localeCompare(b.name, 'th'),
    );
    if (!query) return sorted;
    return sorted.filter((c) => c.name.toLowerCase().includes(query));
  }

  get selectedClientCount(): number {
    return this.selectedClientIds.size;
  }

  onInputFocus(menu: string): void {
    if (this.openDropdown && this.openDropdown !== menu) {
      this.closeDropdowns();
    }
    this.openDropdown = menu;
  }

  onClickInsideModal(event: Event): void {
    event.stopPropagation();
    this.closeDropdowns();
  }

  toggleDropdown(dropdown: string, event: Event): void {
    event.stopPropagation();
    if (this.openDropdown === dropdown) {
      this.closeDropdowns();
    } else {
      if (this.openDropdown) this.closeDropdowns();
      this.openDropdown = dropdown;
    }
  }

  selectRole(opt: { id: number; name: string }): void {
    this.cpRole = opt.id;
    this.searchRoleText = opt.name;
    this.openDropdown = null;
  }

  toggleSite(siteId: number): void {
    if (this.selectedSiteIds.has(siteId)) {
      this.selectedSiteIds.delete(siteId);
    } else {
      this.selectedSiteIds.add(siteId);
    }
  }

  isSiteSelected(siteId: number): boolean {
    return this.selectedSiteIds.has(siteId);
  }

  toggleClient(clientId: number): void {
    if (this.selectedClientIds.has(clientId)) {
      this.selectedClientIds.delete(clientId);
    } else {
      this.selectedClientIds.add(clientId);
    }
    this.clientAccessUnrestricted = this.selectedClientIds.size === 0;
  }

  isClientSelected(clientId: number): boolean {
    return this.selectedClientIds.has(clientId);
  }

  setUnrestrictedAccess(): void {
    this.selectedClientIds.clear();
    this.clientAccessUnrestricted = true;
    this.searchClientText = '';
  }

  clearClientSearch(): void {
    this.searchClientText = '';
  }

  onClose() {
    this.close.emit();
  }

  setPasswordMode(mode: BackofficePasswordMode): void {
    this.passwordMode = mode;
    if (mode === 'self_set') {
      this.password = '';
      this.confirmPassword = '';
    }
  }

  setResetPasswordMode(mode: BackofficePasswordMode): void {
    this.resetPasswordMode = mode;
    if (mode === 'self_set') {
      this.resetPassword = '';
      this.resetConfirmPassword = '';
    }
  }

  onSave() {
    if (this.isCreate && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้างผู้ใช้');
      return;
    }
    if (!this.isCreate && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }

    if (!this.login.trim()) {
      this.toast.error('กรุณาระบุ Login');
      return;
    }

    if (!this.displayName.trim()) {
      this.toast.error('กรุณาระบุชื่อที่แสดง');
      return;
    }

    if (!this.cpRole || this.cpRole <= 0) {
      this.toast.error('กรุณาเลือก Role');
      return;
    }

    if (this.isCreate && this.passwordMode === 'admin_set') {
      if (!this.password || this.password !== this.confirmPassword) {
        this.toast.error('กรุณายืนยันรหัสผ่านให้ตรงกัน');
        return;
      }
    }

    const maxSessions = this.parseMaxSessions();
    if (this.maxConcurrentSessionsText.trim() && maxSessions == null) {
      this.toast.error('จำนวนเครื่องพร้อมกันต้องเป็นตัวเลขตั้งแต่ 1');
      return;
    }

    this.isLoading.set(true);

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    const siteIdStr = Array.from(this.selectedSiteIds)
      .sort((a, b) => a - b)
      .join(',');
    const payload = this.userMapper.mapBackofficeUserPayload(
      {
        login: this.login,
        displayName: this.displayName,
        email: this.email,
        phone: this.phone,
        cpRole: this.cpRole,
        siteIdStr,
        stampAllowed: this.stampAllowed,
        stampRanges: this.stampRanges,
        stampMany: this.stampMany,
        active: this.active,
        cityLoginEnabled: this.cityLoginEnabled,
        maxConcurrentSessions: maxSessions,
        passwordMode: this.passwordMode,
        password: this.password,
        mustChangePassword: this.mustChangePassword,
      },
      userId,
      menuId,
      this.isCreate,
    );

    const request$ = this.isCreate
      ? this.apiService.insertBackofficeUser(payload)
      : this.apiService.updateBackofficeUser(this.data!.id, payload);

    request$.subscribe({
      next: (res: {
        isSuccess?: boolean;
        message?: string;
        idReturn?: number;
        IdReturn?: number;
      }) => {
        if (res?.isSuccess) {
          const savedId = this.isCreate
            ? Number(res.idReturn ?? res.IdReturn ?? 0)
            : this.data!.id;
          this.afterSaveUser(savedId, userId, menuId);
        } else {
          this.toast.error(
            res?.message ||
              (this.isCreate ? 'สร้างผู้ใช้ไม่สำเร็จ' : 'แก้ไขข้อมูลไม่สำเร็จ'),
          );
          this.isLoading.set(false);
        }
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
        this.isLoading.set(false);
      },
    });
  }

  onResetPassword(): void {
    if (this.isCreate || !this.data?.id) return;
    if (!this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }

    if (this.resetPasswordMode === 'admin_set') {
      if (
        !this.resetPassword ||
        this.resetPassword !== this.resetConfirmPassword
      ) {
        this.toast.error('กรุณายืนยันรหัสผ่านใหม่ให้ตรงกัน');
        return;
      }
    }

    this.isResettingPassword.set(true);
    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;

    this.apiService
      .resetBackofficeUserPassword(this.data.id, {
        userId,
        menuId,
        passwordMode: this.resetPasswordMode,
        password:
          this.resetPasswordMode === 'admin_set' ? this.resetPassword : null,
        mustChangePassword: this.resetMustChangePassword,
      })
      .subscribe({
        next: (res: { isSuccess?: boolean; message?: string }) => {
          if (res?.isSuccess) {
            this.toast.success('อัปเดตรหัสผ่านเรียบร้อย');
            this.hasCityPassword = this.resetPasswordMode === 'admin_set';
            this.resetPassword = '';
            this.resetConfirmPassword = '';
            this.saveSuccess.emit();
          } else {
            this.toast.error(res?.message || 'อัปเดตรหัสผ่านไม่สำเร็จ');
          }
          this.isResettingPassword.set(false);
        },
        error: () => {
          this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
          this.isResettingPassword.set(false);
        },
      });
  }

  private afterSaveUser(
    savedUserId: number,
    actingUserId: number,
    menuId: number,
  ): void {
    const siteId = this.selectedSiteId;
    if (!savedUserId || !siteId) {
      this.toast.success(
        this.isCreate ? 'สร้างผู้ใช้สำเร็จ' : 'บันทึกข้อมูลผู้ใช้สำเร็จ',
      );
      this.saveSuccess.emit();
      this.close.emit();
      this.isLoading.set(false);
      return;
    }
    this.saveClientAccess(savedUserId, actingUserId, menuId, siteId);
  }

  private parseMaxSessions(): number | null {
    const raw = this.maxConcurrentSessionsText.trim();
    if (!raw) return null;
    const value = Number(raw);
    return Number.isInteger(value) && value >= 1 ? value : null;
  }

  private loadDetail(userId: number): void {
    this.apiService.getBackofficeUserById(userId).subscribe({
      next: (dto) => {
        console.log(JSON.stringify(dto, null, 2));
        const detail = this.userMapper.mapBackofficeUserDetail(dto);
        this.login = detail.login;
        this.displayName = detail.displayName;
        this.email = detail.email;
        this.phone = detail.phone;
        this.active = detail.active;
        this.cityLoginEnabled = detail.cityLoginEnabled;
        this.hasCityPassword = detail.hasCityPassword;
        this.mustChangePassword = detail.mustChangePassword;
        this.maxConcurrentSessionsText = detail.maxConcurrentSessions
          ? String(detail.maxConcurrentSessions)
          : '';
        this.cpRole = detail.cpRole;
        this.stampAllowed = detail.stampAllowed;
        this.stampRanges = detail.stampRanges;
        this.stampMany = detail.stampMany;
        this.selectedSiteIds = new Set(this.parseSiteIds(detail.siteIdStr));
        const currentRole = this.roleOptions().find(
          (o) => o.id === this.cpRole,
        );
        if (currentRole) this.searchRoleText = currentRole.name;
      },
      error: () => {
        this.toast.error('ไม่สามารถโหลดรายละเอียดผู้ใช้ได้');
      },
    });
  }

  private saveClientAccess(
    targetUserId: number,
    userId: number,
    menuId: number,
    siteId: number,
  ): void {
    const clientIds = this.clientAccessUnrestricted
      ? []
      : Array.from(this.selectedClientIds);
    this.apiCardQuota
      .saveUserClientAccess({
        resUserId: targetUserId,
        siteId,
        userId,
        menuId,
        clientIds,
      })
      .subscribe({
        next: (res) => {
          if (res.code === '000' || res.isSuccess) {
            this.toast.success(
              this.isCreate ? 'สร้างผู้ใช้สำเร็จ' : 'บันทึกข้อมูลผู้ใช้สำเร็จ',
            );
            this.saveSuccess.emit();
            this.close.emit();
          } else {
            this.toast.error(res.message || 'บันทึกสิทธิ์ร้านไม่สำเร็จ');
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          this.toast.error(err?.error?.message || 'บันทึกสิทธิ์ร้านล้มเหลว');
          this.isLoading.set(false);
        },
      });
  }

  private loadRoleOptions(): void {
    this.isOptionsLoading.set(true);
    this.apiService.getAccessRolesForTable().subscribe({
      next: (dtos: AccessRoleDto[]) => {
        const options = (dtos || [])
          .filter((r) => r.status)
          .map((r) => {
            const role = this.roleMapper.mapAccessRoleFromDto(r);
            return { id: role.id, name: role.name };
          });
        this.roleOptions.set(options);

        const currentRole = options.find((o) => o.id === this.cpRole);
        this.searchRoleText = currentRole ? currentRole.name : '';

        this.isOptionsLoading.set(false);
      },
      error: () => {
        this.roleOptions.set([]);
        this.isOptionsLoading.set(false);
      },
    });
  }

  private loadSiteOptions(): void {
    this.isSitesLoading.set(true);
    this.apiService.getParkingSiteOptions().subscribe({
      next: (list) => {
        const mapped = (list || []).map((s) =>
          this.userMapper.mapSiteOption(s),
        );
        this.siteOptions.set(mapped);
        this.isSitesLoading.set(false);
      },
      error: () => {
        this.siteOptions.set([]);
        this.isSitesLoading.set(false);
      },
    });
  }

  private loadClientAccessData(): void {
    const siteId = this.selectedSiteId;
    const targetUserId = this.data?.id;
    if (!siteId || !targetUserId) return;

    this.isClientAccessLoading.set(true);
    this.apiLegacy.GetClientIInfo(siteId).subscribe({
      next: (clients) => {
        this.siteClients = clients ?? [];
        this.apiCardQuota
          .getUserClientAccessForAdmin(targetUserId, siteId)
          .subscribe({
            next: (res) => {
              const raw = res as Record<string, unknown>;
              const ids = (raw['clientIds'] ??
                raw['ClientIds'] ??
                []) as number[];
              this.selectedClientIds = new Set(
                ids.map((id) => Number(id)).filter((id) => id > 0),
              );
              this.clientAccessUnrestricted = this.selectedClientIds.size === 0;
              this.isClientAccessLoading.set(false);
            },
            error: () => {
              this.selectedClientIds.clear();
              this.clientAccessUnrestricted = true;
              this.isClientAccessLoading.set(false);
            },
          });
      },
      error: () => {
        this.siteClients = [];
        this.isClientAccessLoading.set(false);
      },
    });
  }

  private parseSiteIds(siteIdStr: string): number[] {
    if (!siteIdStr?.trim()) return [];
    return siteIdStr
      .split(',')
      .map((v) => Number(v.trim()))
      .filter((id) => id > 0);
  }

  get canShowActivityHistory(): boolean {
    return !!this.data?.id && this.data.id > 0 && !this.isCreate;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
